import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useRealWeddingReducer, useSelectedCity } from "../../../Utility";
import { onGetRealWeddings } from "../../../Store/Slices/realWeddingSlice";
import { pages } from "../../../config/appData";
import PageHero from "../../components/PageHero";
import "./BlogPage.css";

function matchesCity(item, city) {
  if (!city) return true;
  const itemCity = (item?.city ?? "").trim().toLowerCase();
  return itemCity === city.trim().toLowerCase();
}

function BlogPage() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const storedCity = useSelectedCity();
  const selectedCity = (searchParams.get("city") || storedCity || "").trim();
  const { getListData, getListLoading, getListError } = useRealWeddingReducer();

  useEffect(() => {
    dispatch(onGetRealWeddings());
  }, [dispatch]);

  const blogs = useMemo(() => {
    const fullList = Array.isArray(getListData) ? getListData : [];
    return fullList.filter(
      (item) => item?.content_type === "blog" && matchesCity(item, selectedCity),
    );
  }, [getListData, selectedCity]);

  const heroSubtitle = selectedCity
    ? `Expert advice, inspiration, and tips to help you plan your perfect wedding in ${selectedCity}`
    : pages.blog.subtitle;

  const getCardImage = (item) => {
    if (item?.cover_photo && typeof item.cover_photo === "string" && !item.cover_photo.startsWith("C:\\")) {
      return item.cover_photo;
    }
    const photos = item?.wedding_photos;
    if (Array.isArray(photos) && photos.length > 0) {
      const cover = photos.find((p) => p?.image_type === "cover");
      const url = cover?.image_url ?? photos[0]?.image_url;
      if (url && typeof url === "string" && !url.startsWith("C:\\")) return url;
    }
    return null;
  };

  const decodeHtmlEntities = (value) => {
    if (!value || typeof value !== "string") return "";
    const textarea = document.createElement("textarea");
    let decoded = value;
    // Decode twice to handle both "&nbsp;" and "&amp;nbsp;" style payloads.
    for (let i = 0; i < 2; i += 1) {
      textarea.innerHTML = decoded;
      decoded = textarea.value;
    }
    return decoded;
  };

  const cleanText = (value) => {
    if (!value || typeof value !== "string") return "";
    const withoutTags = value.replace(/<[^>]*>/g, " ");
    const decoded = decodeHtmlEntities(withoutTags);
    return decoded
      .replace(/\u00a0/g, " ")
      .replace(/[\u00ad\u200b\u2060\u200c\u200d\ufeff]/g, "")
      .replace(/([A-Za-z0-9])[\r\n\t]+([A-Za-z0-9])/g, "$1$2")
      .replace(/\s+/g, " ")
      .trim();
  };

  const getExcerpt = (item) => {
    const desc = item?.description ?? "";
    const plain = cleanText(desc);
    return plain ? plain.slice(0, 180) + (plain.length > 180 ? "…" : "") : "";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      if (Number.isNaN(d.getTime())) return "";
      return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    } catch {
      return "";
    }
  };

  if (getListLoading) {
    return (
      <div className="blog-page">
        <PageHero
          title="Wedding Blog"
          subtitle={heroSubtitle}
        />
        <div className="blog-container">
          <div className="blog-main">
            <div className="blog-grid">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="blog-card blog-card-skeleton" aria-hidden="true">
                  <div className="blog-card-image-wrapper blog-card-skeleton-image" />
                  <div className="blog-card-content">
                    <div className="blog-card-skeleton-line" />
                    <div className="blog-card-skeleton-line short" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (getListError) {
    return (
      <div className="blog-page">
        <PageHero
          title="Wedding Blog"
          subtitle={heroSubtitle}
        />
        <div className="blog-container">
          <p className="blog-error">Unable to load blog posts. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      <PageHero
        title="Wedding Blog"
        subtitle={heroSubtitle}
      />
      <div className="blog-container">
        <div className="blog-main">
          <div className="blog-grid">
            {blogs.map((blog) => {
              const id = blog?.id ?? blog?.real_wedding_id;
              const imageUrl = getCardImage(blog);
              const title = cleanText(blog?.heading) || "Blog post";
              const excerpt = getExcerpt(blog);
              const category = cleanText(blog?.category);
              const dateStr = formatDate(blog?.published_at);
              const readTime = blog?.read_time_minutes != null ? `${blog.read_time_minutes} min read` : "";
              const author = cleanText(blog?.author) || "WeddingzVenue Team";
              if (!id) return null;
              const slug = typeof blog?.slug === "string" ? blog.slug.trim() : "";
              const blogPathSegment = slug ? encodeURIComponent(slug) : String(id);
              const blogDetailPath = `/blog/${blogPathSegment}`;
              return (
                <Link
                  key={id}
                  className="blog-card"
                  to={blogDetailPath}
                  aria-label={`Read ${title}`}
                >
                  <div className="blog-card-image-wrapper">
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={title}
                        className="blog-card-image"
                        loading="lazy"
                      />
                    ) : (
                      <div className="blog-card-image-placeholder">
                        <span>No image</span>
                      </div>
                    )}
                    {category && (
                      <span className="blog-card-category">{category}</span>
                    )}
                  </div>
                  <div className="blog-card-content">
                    <div className="blog-card-meta">
                      {dateStr && <span className="blog-card-date">{dateStr}</span>}
                      {readTime && (
                        <span className="blog-card-read-time">
                          {dateStr ? " · " : ""}{readTime}
                        </span>
                      )}
                    </div>
                    <h2 className="blog-card-title">{title}</h2>
                    {excerpt && <p className="blog-card-excerpt content">{excerpt}</p>}
                    <div className="blog-card-footer">
                      <span className="blog-card-author">By {author}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          {blogs.length === 0 && (
            <p className="blog-empty">
              {selectedCity
                ? `No blog posts found for ${selectedCity}. Try selecting another city or check back soon.`
                : "No blog posts yet. Check back soon."}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default BlogPage;
